import { Controller, type Control } from 'react-hook-form';
import { StatusPicker } from '@/features/tasks/public';
import type { ProjectStatus } from '@/features/tasks/public';
import type { QuickTaskDraftFormValues } from '../schemas';

interface TaskFieldStatusEditorProps {
  control: Control<QuickTaskDraftFormValues>;
  boardId: string;
  projectStatuses: ProjectStatus[];
  /** The column being created, when the proposal named one the board does not have. */
  pending?: { label: string; color: string | null } | null;
  testIdPrefix: string;
}

/**
 * The board's own status picker rather than a plain list: a proposal naming a column the board
 * does not have can be created here instead of sending the user away.
 *
 * That picker reads the current column off the board, though, and a column being created is not
 * on it — its trigger collapsed to a bare dot, so the editor looked empty. A pending value gets a
 * trigger of its own, naming what will be created in the colour it will be created with, and
 * still opens the list.
 */
export function TaskFieldStatusEditor({
  control,
  boardId,
  projectStatuses,
  pending,
  testIdPrefix,
}: TaskFieldStatusEditorProps) {
  return (
    <Controller
      name="statusId"
      control={control}
      render={({ field: statusField }) => (
        <StatusPicker
          boardId={boardId}
          currentStatus={projectStatuses.find((status) => status.id === statusField.value)}
          onSelect={(statusId) => statusField.onChange(statusId)}
          trigger={
            pending ? (
              <button
                type="button"
                data-testid={`${testIdPrefix}-statusId-pending`}
                className="flex items-center gap-1.5 text-xs transition-opacity hover:opacity-70"
              >
                <span
                  className="size-2 shrink-0 rounded-full"
                  style={{ background: pending.color ?? 'var(--muted-foreground)' }}
                  aria-hidden
                />
                {pending.label}
              </button>
            ) : undefined
          }
        />
      )}
    />
  );
}
